import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { MediaProvider } from './context/MediaContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { AuthProvider as RsyncAuthProvider, AuthCallback } from '@honeyhimself/rsync-auth-react';
import { SidebarProvider } from './context/SidebarContext';
import { Layout } from './components/layout/Layout';
import { DashboardLayout } from './components/layout/DashboardLayout';
import './index.css';
import { Loader2 } from 'lucide-react';
import { ErrorBoundary } from './components/ui/ErrorBoundary';

const Home = lazy(() => import('./pages/Home'));
const Lobby = lazy(() => import('./pages/Lobby'));
const Room = lazy(() => import('./pages/Room'));
const Contact = lazy(() => import('./pages/Contact'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Feedback = lazy(() => import('./pages/Feedback'));
const Pipeline = lazy(() => import('./pages/Pipeline'));
const CandidatePool = lazy(() => import('./pages/recruiter/CandidatePool'));
const RSVP = lazy(() => import('./pages/RSVP'));
const Login = lazy(() => import('./pages/Login'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CandidateDashboard = lazy(() => import('./pages/candidate/CandidateDashboard'));
const Practice = lazy(() => import('./pages/candidate/Practice'));
const PracticeProblems = lazy(() => import('./pages/candidate/PracticeProblems'));
const PracticeRoom = lazy(() => import('./pages/candidate/PracticeRoom'));
const Applications = lazy(() => import('./pages/candidate/Applications'));
const Screening = lazy(() => import('./pages/candidate/Screening'));
const CandidateLandingPage = lazy(() => import('./pages/CandidateLandingPage'));
const DeclineConfirmation = lazy(() => import('./pages/candidate/DeclineConfirmation'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <RsyncAuthProvider
        issuer={import.meta.env.VITE_RSYNC_ISSUER ?? ''}
        clientId={import.meta.env.VITE_RSYNC_CLIENT_ID ?? ''}
        redirectUri={`${window.location.origin}/auth/callback`}
      >
      <AuthProvider>
        <MediaProvider>
          <SidebarProvider>
            <Router>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route element={<Layout />}>
                    {/* Public Routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/candidate" element={<CandidateLandingPage />} />
                    <Route path="/onboarding" element={<Onboarding />} />

                    {/* Rsync OAuth callback — exchanges code and redirects to /login */}
                    <Route path="/auth/callback" element={<AuthCallback redirectTo="/login" />} />

                    {/* Protected Routes - Recruiter */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute allowedRoles={['recruiter']}>
                          <ErrorBoundary>
                            <DashboardLayout />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<Dashboard />} />
                      <Route path="pipeline" element={<Pipeline />} />
                      <Route path="pool" element={<CandidatePool />} />
                    </Route>

                    {/* Protected Routes - Candidate */}
                    {/* Protected Routes - Candidate */}
                    <Route
                      path="/candidate/dashboard"
                      element={
                        <ProtectedRoute allowedRoles={['candidate']}>
                          <ErrorBoundary>
                            <DashboardLayout />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<CandidateDashboard />} />
                      <Route path="applications" element={<Applications />} />
                      <Route path="practice" element={<Practice />} />
                    </Route>

                    {/* Public Candidate Practice Routes */}
                    <Route path="/candidate/practice" element={<PracticeProblems />} />
                    <Route path="/candidate/practice/:id" element={<PracticeRoom />} />

                    {/* Public RSVP Route */}
                    <Route path="/rsvp" element={<RSVP />} />

                    {/* Public Screening Route */}
                    <Route path="/candidate/screening" element={<Screening />} />

                    {/* Public Decline Confirmation Route */}
                    <Route path="/candidate/decline/:token" element={<DeclineConfirmation />} />

                    {/* Public Meeting Routes */}
                    {/* Note: User requested all pages to have layout. */}
                    <Route path="/lobby/:roomId" element={<Lobby />} />
                    <Route path="/room/:roomId" element={<Room />} />
                    <Route path="/feedback/:roomId" element={<Feedback />} />
                  </Route>
                </Routes>
              </Suspense>
            </Router>
          </SidebarProvider>
        </MediaProvider>
      </AuthProvider>
      </RsyncAuthProvider>
    </ThemeProvider>
  );
}
export default App;
